import { useContext, useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";

import useCheckoutPageProps from "@hooks/useCheckoutPageProps.ts";

import { useCheckoutContext } from "@/hooks/useCheckoutContext";
import { CheckoutContext } from "@/providers/context/CheckoutContext";
import { ClassesEnum } from "@/ts/enums/ClassesEnum";
import { getTrackingId } from "@/utils/utils";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
    Button,
    Drawer as ChakraDrawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
    Flex,
    FormControl,
    FormLabel
} from "@chakra-ui/react";

import {
    CountryOption,
    CountryValueContainer,
    Select
} from "@components/Select";
import { Text } from "@components/Text";

interface PropTypes {
    isOpen: boolean;
    onClose: () => void;
}

interface LocLangFormData {
    location: string;
    language: string;
}

// Default Language for all countries
const defaultLanguage = {
    value: "en",
    label: "English"
};

const LocLangSelect = ({ isOpen, onClose }: PropTypes) => {
    const {
        i18n: { changeLanguage },
        t
    } = useTranslation();

    const DrawerRefBtn = useRef(null);

    const { availableCountries } = useCheckoutPageProps();

    const { fetchProductFamilies } = useCheckoutContext();

    const checkoutContext = useContext(CheckoutContext);

    const {
        userSelectedCountry,
        userSelectedLang,
        onUpdateUserSelectedCountry,
        onUpdateUserSelectedLang
    } = checkoutContext;

    const { control, handleSubmit, setValue } = useForm<LocLangFormData>();

    const getLangForCountry = () => {
        return (
            availableCountries.find(o => o.value === userSelectedCountry)
                ?.languages ?? []
        );
    };

    const selectedLanguage = (langId: string) => {
        const language =
            getLangForCountry().filter(lang => lang.value === langId)[0] ??
            null;

        if (language)
            return {
                value: language.value,
                label: language.label
            };

        return defaultLanguage;
    };

    useEffect(() => {
        setValue("language", userSelectedLang);
        setValue("location", userSelectedCountry);
    }, [userSelectedCountry, userSelectedLang]);

    const onSubmitLocLangForm = async ({
        language,
        location
    }: LocLangFormData) => {
        changeLanguage(language);
        onUpdateUserSelectedCountry(location);
        onUpdateUserSelectedLang(language);
        await fetchProductFamilies(location, language);
        onClose();
    };

    return (
        <ChakraDrawer
            isOpen={isOpen}
            placement="right"
            onClose={onClose}
            finalFocusRef={DrawerRefBtn}
            size={["full", null, "md"]}
            variant="primary"
        >
            <DrawerOverlay />
            <DrawerContent>
                <DrawerCloseButton>
                    <FontAwesomeIcon
                        icon={faXmark}
                        color="var(--euka-colors-gray-900)"
                        fontSize={22}
                    />
                </DrawerCloseButton>
                <DrawerHeader mt={5}>
                    <Text type="subHeading">
                        {t(
                            "checkout.locLangSelectDrawer.title",
                            "Select language"
                        )}
                    </Text>
                </DrawerHeader>

                <DrawerBody mt={12}>
                    {/** Language Location Form */}
                    <Flex direction="column" as="form" gap={10}>
                        <Controller
                            name="location"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <FormControl>
                                    <FormLabel>
                                        <Trans>Your location</Trans>
                                    </FormLabel>
                                    <Select
                                        option={CountryOption}
                                        valueContainer={CountryValueContainer}
                                        value={
                                            availableCountries.find(c => {
                                                return c.value === value;
                                            }) ?? null
                                        }
                                        onChange={v => {
                                            onChange(v?.value);
                                        }}
                                        options={availableCountries}
                                        placeholder={
                                            <Trans>Your location</Trans>
                                        }
                                        isDisabled
                                        dataTrackingId={getTrackingId(
                                            [
                                                ClassesEnum.LocationSelectDrawer,
                                                ClassesEnum.Select
                                            ],
                                            "selectLocation"
                                        )}
                                    />
                                </FormControl>
                            )}
                        />
                        <Controller
                            name="language"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <FormControl>
                                    <FormLabel>
                                        {t(
                                            "checkout.locLangSelectDrawer.selectLangLabel",
                                            "Select your language"
                                        )}
                                    </FormLabel>
                                    <Select
                                        value={selectedLanguage(value)}
                                        onChange={v => onChange(v?.value)}
                                        options={getLangForCountry()}
                                        placeholder={t(
                                            "checkout.locLangSelectDrawer.selectLang",
                                            "Select your language"
                                        )}
                                        dataTrackingId={getTrackingId(
                                            [
                                                ClassesEnum.LocationSelectDrawer,
                                                ClassesEnum.Select
                                            ],
                                            "selectLanguage"
                                        )}
                                    />
                                </FormControl>
                            )}
                        />
                    </Flex>
                </DrawerBody>

                <DrawerFooter>
                    <Button
                        mr="auto"
                        colorScheme="ebonyClay"
                        onClick={handleSubmit(onSubmitLocLangForm)}
                        data-tracking-id={getTrackingId(
                            [
                                ClassesEnum.LocationSelectDrawer,
                                ClassesEnum.Button
                            ],
                            "confirm"
                        )}
                    >
                        <Trans>Confirm changes</Trans>
                    </Button>
                </DrawerFooter>
            </DrawerContent>
        </ChakraDrawer>
    );
};

export default LocLangSelect;
